import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ClipboardList, Plus, CheckCircle2, Edit, Trash2, Clock, AlertCircle, User, Eye } from 'lucide-react';
import { useTasks } from '@/hooks/queries';
import { UserSelector } from '@/components/shared/UserSelector';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { TaskLinkedEntities } from '@/components/shared/TaskLinkedEntities';
import { LinkManager } from '@/components/shared/LinkManager';
import { useLinkStore } from '@/stores/linkStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useEndpointStore } from '@/stores/endpointStore';
import { useCloudServiceStore } from '@/stores/cloudServiceStore';
import { useSupplierStore } from '@/stores/supplierStore';
import { useEntityNameResolver } from '@/hooks/useEntityNameResolver';
import type { EntityType } from '@/types/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { TaskCadence } from '@/types/tasks';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'task');

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  cadence: z.enum(['once', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'custom_rrule']),
  rrule: z.string().optional(),
  owner_id: z.string().optional(),
  due_date: z.string().min(1, 'Due date is required'),
});

type TaskFormData = z.infer<typeof taskSchema>;

const statusColors = {
  Scheduled: 'bg-secondary text-secondary-foreground',
  Due: 'bg-warning text-warning-foreground',
  Overdue: 'bg-danger text-danger-foreground',
  Done: 'bg-success text-success-foreground',
  Skipped: 'bg-muted text-muted-foreground',
};

export default function TasksPage() {
  const { organization, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);
  
  const { bulkCreateLinks, loadLinks, links } = useLinkStore();
  const { resolveEntityName } = useEntityNameResolver();
  
  // Load entity stores for name resolution
  const { loadSites } = useSiteStore();
  const { loadZones } = useZoneStore();
  const { loadSystems } = useSystemStore();
  const { loadEndpoints } = useEndpointStore();
  const { loadCloudServices } = useCloudServiceStore();
  const { loadSuppliers } = useSupplierStore();

  // Load links and entity data on mount for TaskLinkedEntities to display names
  useEffect(() => {
    loadLinks();
    loadSites();
    loadZones();
    loadSystems();
    loadEndpoints();
    loadCloudServices();
    loadSuppliers();
  }, [loadLinks, loadSites, loadZones, loadSystems, loadEndpoints, loadCloudServices, loadSuppliers]);

  const {
    tasks,
    isLoading,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    isCreating,
    isUpdating,
    isDeleting,
  } = useTasks(organization?.id, undefined, profile?.user_id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      cadence: 'monthly',
      due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
    },
  });

  const watchedCadence = watch('cadence');
  const selectedTask = tasks?.find(t => t.id === selectedTaskId);

  const filteredTasks = tasks?.filter(task => {
    if (filterAssigned && task.owner_id !== profile?.user_id) return false;
    if (statusFilter === 'all') return true;
    return task.status === statusFilter;
  }) || [];

  const overdueCount = tasks?.filter(t => t.status === 'Overdue').length || 0;
  const dueCount = tasks?.filter(t => t.status === 'Due').length || 0;
  const scheduledCount = tasks?.filter(t => t.status === 'Scheduled').length || 0;
  const completedCount = tasks?.reduce((sum, t) => sum + (t.times_completed || 0), 0) || 0;

  const handleCreate = async (data: TaskFormData) => {
    if (!organization?.id) return;
    
    const result = await createTask({
      title: data.title,
      description: data.description || '',
      cadence: data.cadence,
      rrule: data.rrule,
      owner_id: data.owner_id,
      due_next: dayjs(data.due_date).toISOString(),
      status: 'Scheduled',
      org_id: organization.id,
    });
    
    // Create links if any were selected
    if (result?.id && selectedLinks.length > 0) {
      const linksByType = selectedLinks.reduce((acc, link) => {
        if (!acc[link.type]) acc[link.type] = [];
        acc[link.type].push(link.id);
        return acc;
      }, {} as Record<EntityType, string[]>);
      
      // Create links for each entity type
      for (const [targetType, targetIds] of Object.entries(linksByType)) {
        await bulkCreateLinks('task', result.id, targetType as EntityType, targetIds);
      }
    }
    
    reset();
    setSelectedLinks([]);
    setCreateDialogOpen(false);
  };

  const handleEdit = async (data: TaskFormData) => {
    if (!selectedTaskId || !organization?.id) return;

    updateTask({
      id: selectedTaskId,
      data: {
        title: data.title,
        description: data.description,
        cadence: data.cadence,
        rrule: data.rrule,
        owner_id: data.owner_id,
        due_next: dayjs(data.due_date).toISOString(),
      },
    });

    // Handle link updates - create new links for newly selected entities
    if (selectedLinks.length > 0) {
      const linksByType = selectedLinks.reduce((acc, link) => {
        if (!acc[link.type]) acc[link.type] = [];
        acc[link.type].push(link.id);
        return acc;
      }, {} as Record<EntityType, string[]>);
      
      for (const [targetType, targetIds] of Object.entries(linksByType)) {
        await bulkCreateLinks('task', selectedTaskId, targetType as EntityType, targetIds);
      }
    }

    reset();
    setSelectedLinks([]);
    setEditDialogOpen(false);
    setSelectedTaskId(null);
  };

  const handleDelete = () => {
    if (!selectedTaskId) return;
    deleteTask(selectedTaskId);
    setDeleteDialogOpen(false);
    setSelectedTaskId(null);
  };

  const handleComplete = (taskId: string) => {
    completeTask({ id: taskId });
  };

  const canCompleteTask = (task: NonNullable<typeof tasks>[0]) => {
    return task.owner_id === profile?.user_id || profile?.role === 'admin';
  };

  const openEditDialog = async (taskId: string) => {
    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;

    reset({
      title: task.title,
      description: task.description || '',
      cadence: task.cadence as TaskCadence,
      rrule: task.rrule || '',
      owner_id: task.owner_id || undefined,
      due_date: dayjs(task.due_next).format('YYYY-MM-DD'),
    });

    // Load existing links for this task
    await loadLinks('task', taskId);
    const taskLinks = links.filter(l => 
      (l.source_type === 'task' && l.source_id === taskId) ||
      (l.target_type === 'task' && l.target_id === taskId)
    );
    
    // Map to SelectedEntity format with resolved names
    const existingLinks: SelectedEntity[] = taskLinks.map(l => {
      const isSource = l.source_type === 'task';
      const type = (isSource ? l.target_type : l.source_type) as EntityType;
      const id = isSource ? l.target_id : l.source_id;
      return {
        id,
        type,
        name: resolveEntityName(type, id),
      };
    });
    setSelectedLinks(existingLinks);

    setSelectedTaskId(taskId);
    setEditDialogOpen(true);
  };

  const openDetailSheet = (taskId: string) => {
    setSelectedTaskId(taskId);
    setDetailSheetOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tasks</h1>
            <p className="text-muted-foreground">Manage recurring tasks and maintenance schedules</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tasks</h1>
          <p className="text-muted-foreground">Manage recurring tasks and maintenance schedules</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-danger" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overdueCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Soon</CardTitle>
            <Clock className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dueCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scheduled</CardTitle>
            <ClipboardList className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{scheduledCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
            <SelectItem value="Due">Due Soon (7 days)</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Done">Done</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={filterAssigned ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterAssigned) {
              searchParams.delete('filter');
            } else {
              searchParams.set('filter', 'assigned');
            }
            setSearchParams(searchParams);
          }}
        >
          <User className="h-4 w-4 mr-2" />
          Assigned to me
        </Button>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-10">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No tasks found</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Task
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map((task) => (
            <Card key={task.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetailSheet(task.id)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{task.title}</CardTitle>
                      <Badge className={statusColors[task.status]}>{task.status}</Badge>
                      <Badge variant="outline">{task.cadence}</Badge>
                    </div>
                    {task.description && (
                      <CardDescription>{task.description}</CardDescription>
                    )}
                    {/* Show linked entities */}
                    <div className="mt-2">
                      <TaskLinkedEntities taskId={task.id} maxDisplay={3} />
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetailSheet(task.id)}
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {task.status !== 'Done' && canCompleteTask(task) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleComplete(task.id)}
                        title="Mark as complete"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(task.id)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedTaskId(task.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {task.owner && (
                    <div>
                      <p className="text-muted-foreground">Owner</p>
                      <p className="font-medium">{task.owner.name || task.owner.email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium">{dayjs(task.due_next).format('MMM D, YYYY')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Completions</p>
                    <p className="font-medium">{task.times_completed || 0}</p>
                  </div>
                  {task.last_completed_at && (
                    <div>
                      <p className="text-muted-foreground">Last Completed</p>
                      <p className="font-medium">
                        {dayjs(task.last_completed_at).format('MMM D, YYYY')}
                        {task.last_completed_by_profile && (
                          <span className="text-muted-foreground text-xs block">
                            by {task.last_completed_by_profile.name || task.last_completed_by_profile.email}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {task.related_to_type && task.related_to_id && (
                    <div>
                      <p className="text-muted-foreground">Related To</p>
                      <p className="font-medium capitalize">{task.related_to_type}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) {
          reset();
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
          setSelectedTaskId(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editDialogOpen ? 'Edit Task' : 'Create Task'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit(editDialogOpen ? handleEdit : handleCreate)} className="space-y-4">
            <div>
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="Enter task title"
              />
              {errors.title && (
                <p className="text-sm text-danger">{errors.title.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Describe the task"
                className="min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cadence">Cadence *</Label>
                <Select
                  value={watchedCadence}
                  onValueChange={(value) => setValue('cadence', value as TaskCadence)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="custom_rrule">Custom (RRULE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="due_date">Next Due Date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  {...register('due_date')}
                />
                {errors.due_date && (
                  <p className="text-sm text-danger">{errors.due_date.message}</p>
                )}
              </div>
            </div>

            {watchedCadence === 'custom_rrule' && (
              <div>
                <Label htmlFor="rrule">RRULE</Label>
                <Input
                  id="rrule"
                  {...register('rrule')}
                  placeholder="FREQ=MONTHLY;INTERVAL=1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter a valid RRULE string for custom recurrence patterns
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="owner">Owner</Label>
              <UserSelector
                value={watch('owner_id')}
                onChange={(owner_id) => setValue('owner_id', owner_id)}
                placeholder="Select owner (optional)"
                allowUnassigned
              />
            </div>

            <EntityLinkSelector
              allowedTypes={ALLOWED_LINK_TYPES}
              onLinksChange={setSelectedLinks}
              initialLinks={selectedLinks}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => {
                reset();
                setSelectedLinks([]);
                setCreateDialogOpen(false);
                setEditDialogOpen(false);
                setSelectedTaskId(null);
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating}>
                {editDialogOpen ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTask?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Task Details
            </SheetTitle>
          </SheetHeader>
          
          {selectedTask && (
            <div className="space-y-6 mt-6">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">{selectedTask.title}</h3>
                <div className="flex gap-2">
                  <Badge className={statusColors[selectedTask.status]}>{selectedTask.status}</Badge>
                  <Badge variant="outline">{selectedTask.cadence}</Badge>
                </div>
                {selectedTask.description && (
                  <p className="text-muted-foreground">{selectedTask.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Due Date</p>
                  <p className="font-medium">{dayjs(selectedTask.due_next).format('MMM D, YYYY')}</p>
                </div>
                {selectedTask.owner && (
                  <div>
                    <p className="text-muted-foreground">Owner</p>
                    <p className="font-medium">{selectedTask.owner.name || selectedTask.owner.email}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Completions</p>
                  <p className="font-medium">{selectedTask.times_completed || 0}</p>
                </div>
                {selectedTask.last_completed_at && (
                  <div>
                    <p className="text-muted-foreground">Last Completed</p>
                    <p className="font-medium">{dayjs(selectedTask.last_completed_at).format('MMM D, YYYY')}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                {selectedTask.status !== 'Done' && canCompleteTask(selectedTask) && (
                  <Button onClick={() => handleComplete(selectedTask.id)} className="flex-1">
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Complete
                  </Button>
                )}
                <Button variant="outline" onClick={() => {
                  setDetailSheetOpen(false);
                  openEditDialog(selectedTask.id);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>

              {/* LinkManager for managing relationships */}
              <LinkManager
                entityType="task"
                entityId={selectedTask.id}
                entityName={selectedTask.title}
                allowedTypes={ALLOWED_LINK_TYPES}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
